import styled from "@emotion/styled";
import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import Button from "@components/Button";
import Text from "@components/Text";
import { Column, Row } from "@components/Flex";
import SizedBox from "@components/SizedBox";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import { Hint, Root, Title, ToolButton } from "@screens/FieldsScreen/YieldAdjustPanel";
import {
  useArea,
  useConvert,
  useLang,
  useLangCode,
  useWeight,
  useYield,
} from "@src/hooks/useLang";
import type { Localizations } from "@src/localizations";
import { WeightUnit } from "@services/demosService";
import type {
  TEveningNoop,
  TEveningPrepare,
  TEveningStrength,
  TEveningSummary,
} from "@services/fieldsService";
import BN, { trunc } from "@src/utils/BN";

/**
 * "Even out yield", by machine: has the season's machine tracks read (a
 * background scan it follows), then previews the result on the map, shows
 * what it changes and applies it. Same slot and look as YieldAdjustPanel; the
 * two never show at once. The map itself is driven by the VM (preview /
 * original / stored) — on desktop the original and the result side by side,
 * once the tracks are read.
 */

type TFieldsLang = Localizations["fields"];

const SummaryLine = styled(Text)`
  font-size: 13px;
  line-height: 20px;
  color: #494c5b;
`;

const Notice = styled(Text)`
  font-size: 12px;
  line-height: 18px;
  color: #7c5e00;
  background: #fef7e0;
  border-radius: 4px;
  padding: 8px 10px;
  box-sizing: border-box;
`;

/**
 * Names the two maps of the side-by-side preview: "Original" on the main map,
 * the result on the pane beside it. Same spot on both panes, under the main
 * map's layer picker, so the two read as a pair.
 */
export const EvenPaneLabel = styled.div`
  position: absolute;
  top: 66px;
  left: 24px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 4px;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.1);
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: #494c5b;
  white-space: nowrap;
  pointer-events: none;
`;

/** Marks the evened side. */
export const EvenPaneDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #68ad18;
  flex-shrink: 0;
`;

/** What an evening was made with, for chips and labels: "by machine", or an earlier strength. */
export const eveningStrengthLabel = (t: TFieldsLang, strength: TEveningStrength): string => {
  switch (strength) {
    case "machine":
      return t.yieldEvenByMachine;
    case "light":
      return t.yieldEvenStrengthLight;
    case "strong":
      return t.yieldEvenStrengthStrong;
    default:
      return t.yieldEvenStrengthMedium;
  }
};

const noopMessage = (t: TFieldsLang, noop: TEveningNoop): string => {
  switch (noop) {
    case "no_tracks":
      return t.yieldEvenNoopNoTracks;
    case "single_machine":
      return t.yieldEvenNoopSingleMachine;
    case "not_enough_data":
      return t.yieldEvenNoopNotEnoughData;
    case "too_large":
      return t.yieldEvenNoopTooLarge;
    default:
      return t.yieldEvenNoopNothingToEven;
  }
};

/** Fills `{name}` placeholders in a translated sentence. */
const fill = (template: string, values: Record<string, string>): string =>
  template.replace(/\{(\w+)\}/g, (match, key: string) => values[key] ?? match);

/** A fraction as a percentage: whole numbers, one decimal below 10%. */
const formatShare = (fraction: number): string => {
  const pct = fraction * 100;
  if (pct > 0 && pct < 0.1) return "<0.1%";
  return `${trunc(pct, Math.abs(pct) < 10 ? 1 : 0)}%`;
};

/** Enough decimals to read a value without false precision. */
const decimalsFor = (value: number): number => {
  const abs = Math.abs(value);
  return abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
};

/** One value rounded for reading, trailing zeros dropped (0.4, 12.3, 1,234). */
const formatAmount = (value: number): string =>
  new BN(trunc(value, decimalsFor(value))).toFormat();

const formatDate = (iso: string, langCode: string): string | null => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return date.toLocaleDateString(langCode, { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return date.toLocaleDateString();
  }
};

/** How much of the scan is done, in whole percent; null while the server can't tell. */
const scanPercent = (progress: { done: number; total: number } | null): number | null => {
  if (!progress || !(progress.total > 0)) return null;
  const pct = Math.floor((progress.done / progress.total) * 100);
  return Math.min(100, Math.max(0, pct));
};

const YieldEvenPanel: React.FC = () => {
  const vm = useFieldsScreenVM();
  const lang = useLang();
  const langCode = useLangCode();
  const locArea = useArea();
  const locWeight = useWeight();
  const locYield = useYield();
  const { convertArea, convertWeight, convertYield } = useConvert();
  const t = lang.fields;

  // Mounted with the map for as long as the screen is: leaving the screen
  // stops following a scan.
  useEffect(() => () => vm.stopYieldEvenPrepare(), [vm]);

  if (!vm.isYieldEvenOpen) return null;

  const busy = vm.yieldEvenBusy;
  const active = vm.selectedFieldEvening;
  // Nothing to apply when the by-machine evening is the one applied.
  const showApply = vm.yieldEvenResultKey !== "stored";
  // An evening made with the earlier method; applying replaces it.
  const legacy = active && active.strength !== "machine" ? active : null;
  // The result can be shown (the machine tracks are read, or it is saved).
  const ready = vm.yieldEvenResultReady;
  const summary = vm.yieldEvenSummary;
  const noop = vm.yieldEvenNoop;
  const failed = vm.yieldEvenFailedToLoad;
  const appliedOn = active && !showApply ? formatDate(active.appliedAt, langCode) : null;

  const handleRemove = () => {
    if (window.confirm(t.yieldEvenRemoveConfirm)) void vm.removeYieldEven();
  };

  /** A notice with "Try again" under it. */
  const renderRetry = (message: string) => (
    <>
      <Notice>{message}</Notice>
      <SizedBox height={8} />
      <Row>
        <ToolButton
          type="button"
          disabled={busy != null || vm.yieldEvenPreparing}
          onClick={vm.retryYieldEvenPrepare}
        >
          {vm.yieldEvenPreparing ? "…" : t.yieldEvenTryAgain}
        </ToolButton>
      </Row>
    </>
  );

  /** Until the preview can be shown: how far reading the machine tracks is. */
  const renderTracksStatus = (prepare: TEveningPrepare | null) => {
    if (vm.yieldEvenPrepareError) return renderRetry(t.yieldEvenScanFailed);
    // Not asked yet, or the answer is on its way.
    if (!prepare) return <Hint>{t.yieldEvenLoading}</Hint>;
    switch (prepare.status) {
      case "scanning": {
        const percent = scanPercent(prepare.progress);
        return (
          <>
            <SummaryLine>
              {percent == null
                ? t.yieldEvenScanning
                : fill(t.yieldEvenScanningPercent, { percent: String(percent) })}
            </SummaryLine>
            <SizedBox height={4} />
            <Hint>{t.yieldEvenScanNote}</Hint>
          </>
        );
      }
      case "failed":
        return renderRetry(
          prepare.error === "busy" ? t.yieldEvenScanBusy : t.yieldEvenScanFailed
        );
      case "unavailable":
        return <Notice>{t.yieldEvenScanUnavailable}</Notice>;
      default:
        return null;
    }
  };

  const renderSummary = (s: TEveningSummary) => {
    // Same conversions as the stats card: centners/ha → the user's yield
    // unit, kg → the user's weight unit, hectares → the user's area unit.
    const avgBefore = convertYield(s.avgBefore);
    const avgAfter = convertYield(s.avgAfter);
    const avgDecimals = decimalsFor(Math.max(Math.abs(avgBefore), Math.abs(avgAfter)));
    const total = convertWeight(s.totalMassKg, WeightUnit.Kilogram);
    return (
      <Column crossAxisSize="max" style={{ gap: 2 }}>
        {s.machines.length > 0 && (
          <SummaryLine>
            {fill(t.yieldEvenMachines, { machines: s.machines.join(", ") })}
          </SummaryLine>
        )}
        <SummaryLine>
          {fill(t.yieldEvenMachineStep, {
            before: formatShare(s.stepBefore),
            after: formatShare(s.stepAfter),
          })}
        </SummaryLine>
        <SummaryLine>
          {fill(t.yieldEvenAdjusted, {
            share: formatShare(s.adjustedShare),
            area: `${formatAmount(convertArea(s.adjustedAreaHa))} ${locArea()}`,
          })}
        </SummaryLine>
        <SummaryLine>
          {fill(t.yieldEvenLargestChange, {
            raise: formatShare(Math.abs(s.maxRaise)),
            cut: formatShare(Math.abs(s.maxCut)),
          })}
        </SummaryLine>
        <SummaryLine>
          {fill(t.yieldEvenAverage, {
            before: new BN(avgBefore).toFormat(avgDecimals),
            after: `${new BN(avgAfter).toFormat(avgDecimals)} ${locYield()}`,
          })}
        </SummaryLine>
        <SummaryLine>
          {fill(t.yieldEvenTotal, { total: `${formatAmount(total)} ${locWeight()}` })}
        </SummaryLine>
      </Column>
    );
  };

  /** The preview, or the evening already applied. */
  const renderResult = () => (
    <>
      {failed && (
        <>
          {renderRetry(t.yieldEvenLoadFailed)}
          <SizedBox height={8} />
        </>
      )}
      {noop ? (
        <Notice>{noopMessage(t, noop)}</Notice>
      ) : summary ? (
        renderSummary(summary)
      ) : (
        !failed && <Hint>{t.yieldEvenLoading}</Hint>
      )}
    </>
  );

  return (
    <Root>
      <Title>{t.yieldEvenTitle}</Title>
      <SizedBox height={4} />
      <Hint>{t.yieldEvenHint}</Hint>
      <SizedBox height={12} />

      {legacy && (
        <>
          <Notice>
            {fill(t.yieldEvenLegacyNotice, {
              date: formatDate(legacy.appliedAt, langCode) ?? legacy.appliedAt,
              strength: eveningStrengthLabel(t, legacy.strength).toLowerCase(),
            })}
          </Notice>
          <SizedBox height={12} />
        </>
      )}

      {/* Side by side the original is always on the main map; a phone flips
          one map instead, once there is a result to flip to. */}
      {!vm.yieldEvenPane && ready && (
        <>
          <Row>
            <ToolButton
              type="button"
              active={vm.yieldEvenShowOriginal}
              aria-pressed={vm.yieldEvenShowOriginal}
              disabled={busy != null}
              onClick={vm.toggleYieldEvenOriginal}
            >
              {t.yieldEvenShowOriginal}
            </ToolButton>
          </Row>
          <SizedBox height={12} />
        </>
      )}

      {ready ? renderResult() : renderTracksStatus(vm.yieldEvenPrepare)}
      {appliedOn && (
        <>
          <SizedBox height={8} />
          <Hint>{fill(t.yieldEvenAppliedOn, { date: appliedOn })}</Hint>
        </>
      )}

      <SizedBox height={12} />
      <Row style={{ gap: 8 }}>
        {showApply && (
          <Button kind="primary" disabled={!vm.yieldEvenCanApply} onClick={vm.applyYieldEven}>
            {busy === "apply" ? "…" : t.yieldEvenApply}
          </Button>
        )}
        <Button kind="secondary" disabled={busy != null} onClick={vm.closeYieldEven}>
          {t.yieldEvenCancel}
        </Button>
      </Row>
      {vm.yieldEvenIsApplied && (
        <>
          <SizedBox height={8} />
          <Button kind="outline" disabled={busy != null} onClick={handleRemove}>
            {busy === "remove" ? "…" : t.yieldEvenRemove}
          </Button>
        </>
      )}
      {showApply && (
        <>
          <SizedBox height={10} />
          <Hint>{t.yieldEvenApplyNote}</Hint>
        </>
      )}
    </Root>
  );
};

export default observer(YieldEvenPanel);
